import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut as firebaseSignOut, type Auth } from 'firebase/auth';
import { clearTenantId } from '@elka/firebase';
import { useTenantClaims } from './useTenantClaims';

export interface UseAuthOptions {
  /**
   * Firebase Auth instance
   */
  auth: Auth;
}

export interface UseAuthReturn {
  user: ReturnType<typeof useAuthState>[0];
  isLoading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  /** Authenticated but has no `tenantId` claim yet (show NotProvisionedScreen). */
  isUnprovisioned: boolean;
  /** Tenant from custom claims, or null until 'ready'. */
  tenantId: string | null;
  /** Office role from custom claims, or null. */
  role: 'admin' | 'user' | null;
  /** Backend `superAdmin` custom claim (payslips + audit-log gate). False until claims are 'ready'. */
  superAdmin: boolean;
}

/**
 * Configurable authentication hook.
 *
 * Authorization is claim-based only: a user is authorized iff they hold a
 * `tenantId` custom claim.
 *
 * @example
 * ```tsx
 * const { user, isAuthorized, isUnprovisioned, signOut } = useAuth({
 *   auth: getAuthInstance(),
 * });
 * ```
 */
export function useAuth(options: UseAuthOptions): UseAuthReturn {
  const { auth } = options;
  const [user, loading, error] = useAuthState(auth);
  const claims = useTenantClaims(auth);

  const hasTenantClaim = claims.status === 'ready';
  const tenantId = claims.status === 'ready' ? claims.tenantId : null;
  const role = claims.status === 'ready' ? claims.role : null;
  const superAdmin = claims.status === 'ready' ? claims.superAdmin : false;
  const isUnprovisioned = claims.status === 'unprovisioned';

  const isAuthorized = hasTenantClaim;

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    } finally {
      clearTenantId();
      // Full reload is LOAD-BEARING: hooks memoize their queries with
      // useMemo(..., []) and the session tenant lives in a module-level variable,
      // so a SPA navigation would keep streaming the previous tenant's data.
      // A hard reload resets both the React tree and the module state.
      window.location.assign('/login');
    }
  };

  return {
    user,
    isLoading: loading || claims.status === 'loading',
    error: error?.message || null,
    signOut,
    isAuthenticated: !!user,
    isAuthorized,
    isUnprovisioned,
    tenantId,
    role,
    superAdmin,
  };
}

/**
 * Check if user is admin based on custom claims or email
 * This is a simple implementation - extend based on your needs
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- placeholder stub for future admin check
export function useIsAdmin(_user: ReturnType<typeof useAuthState>[0]): boolean {
  // Implement your admin check logic here
  // For example, check custom claims or specific email addresses
  return false;
}
